<template>
  <div class="customer-form-container">
    <div class="page-header">
      <el-button link class="back-btn" @click="handleCancel">
        <el-icon><ArrowLeft /></el-icon>
        {{ $t('common.back') }}
      </el-button>
      <span class="page-title">{{ title }}</span>
      <el-tag :type="customerForm.status === 1 ? 'success' : 'info'" size="large">
        {{ statusText }}
      </el-tag>
    </div>
    
    <el-card class="form-card">
      <div class="card-header">
        <span class="header-title">{{ $t('customer.basicInfo') }}</span>
      </div>
      
      <el-form 
        :model="customerForm" 
        :rules="rules" 
        ref="formRef" 
        :label-width="isMobile ? 'auto' : '140px'"
        :label-position="isMobile ? 'top' : 'left'"
        class="customer-form"
      >
        <el-form-item :label="$t('customer.name')" prop="name">
          <el-input 
            v-model="customerForm.name" 
            :placeholder="$t('customer.enterName')"
            clearable
          />
        </el-form-item>

        <el-form-item :label="$t('customer.phone')" prop="phone">
          <el-input 
            v-model="customerForm.phone" 
            :placeholder="$t('customer.enterPhone')"
            clearable
            maxlength="11"
          />
        </el-form-item>

        <el-form-item :label="$t('customer.gender')" prop="gender">
          <el-radio-group v-model="customerForm.gender">
            <el-radio :value="true">{{ $t('customer.male') }}</el-radio>
            <el-radio :value="false">{{ $t('customer.female') }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item :label="$t('customer.age')" prop="age">
          <el-input-number 
            v-model="customerForm.age" 
            :min="5"
            :max="100"
            controls-position="right"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item :label="$t('customer.status')" prop="status">
          <el-select 
            v-model="customerForm.status" 
            :placeholder="$t('customer.selectStatus')"
            style="width: 100%"
          >
            <el-option :label="$t('customer.statusOptions.inactive')" :value="0" />
            <el-option :label="$t('customer.statusOptions.active')" :value="1" />
            <el-option :label="$t('customer.statusOptions.deleted')" :value="2" />
            <el-option :label="$t('customer.statusOptions.blocked')" :value="3" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('customer.remark')" prop="remark">
          <el-input 
            v-model="customerForm.remark" 
            :placeholder="$t('customer.enterRemark')"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            clearable
          />
        </el-form-item>

        <el-form-item>
          <el-button 
            type="primary" 
            @click="submitForm"
            :loading="loading"
            class="submit-btn"
          >
            {{ submitText }}
          </el-button>
          <el-button @click="handleCancel">
            {{ $t('common.cancel') }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowLeft } from '@element-plus/icons-vue'

const router = useRouter()
const { t } = useI18n()
const formRef = ref()
const loading = ref(false)
const hasChanges = ref(false)

// Mobile detection
const isMobile = ref(window.innerWidth <= 768)
const handleResize = () => {
  isMobile.value = window.innerWidth <= 768
}
onMounted(() => {
  window.addEventListener('resize', handleResize)
})
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

// Status text
const statusText = computed(() => {
  const map = {
    0: t('customer.statusOptions.inactive'),
    1: t('customer.statusOptions.active'),
    2: t('customer.statusOptions.deleted'),
    3: t('customer.statusOptions.blocked')
  }
  return map[customerForm.status] || '-'
})

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  submitText: {
    type: String,
    default: ''
  },
  initialData: {
    type: Object,
    default: () => ({})
  },
  onSubmit: {
    type: Function,
    required: true
  },
  loadData: {
    type: Function,
    default: null
  }
})

const defaultForm = {
  id: 0,
  name: '',
  phone: '',
  gender: true,
  age: 18,
  status: 0,
  remark: '',
  avatar: '',
  isAdmin: false
}

const customerForm = reactive({ ...defaultForm })

const validatePhone = (rule, value, callback) => {
  const phoneReg = /^1[3-9]\d{9}$/
  if (!value) {
    callback(new Error(t('customer.form.phoneRequired')))
  } else if (!phoneReg.test(value)) {
    callback(new Error(t('customer.form.phoneInvalid')))
  } else {
    callback()
  }
}

const rules = {
  name: [
    { required: true, message: t('customer.form.nameRequired'), trigger: 'blur' },
    { min: 2, max: 50, message: t('customer.form.nameLength'), trigger: 'blur' }
  ],
  phone: [
    { required: true, validator: validatePhone, trigger: 'blur' }
  ],
  age: [
    { type: 'number', min: 5, max: 100, message: t('customer.form.ageInvalid'), trigger: 'blur' }
  ]
}

let initialFormState = JSON.stringify(defaultForm)

watch(customerForm, (newVal) => {
  hasChanges.value = JSON.stringify(newVal) !== initialFormState
}, { deep: true })

const initForm = () => {
  Object.assign(customerForm, {
    ...defaultForm,
    ...props.initialData
  })
  initialFormState = JSON.stringify(customerForm)
  hasChanges.value = false
}

const submitForm = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        await props.onSubmit(customerForm)
        hasChanges.value = false
      } catch (error) {
        ElMessage.error(error.message || t('customer.form.operationFailed'))
      } finally {
        loading.value = false
      }
    }
  })
}

const handleCancel = async () => {
  if (hasChanges.value) {
    try {
      await ElMessageBox.confirm(
        t('customer.form.unsavedChangesMessage'),
        t('customer.form.unsavedChangesTitle'),
        {
          confirmButtonText: t('customer.form.leave'),
          cancelButtonText: t('customer.form.stay'),
          type: 'warning',
        }
      )
      router.push('/customer')
    } catch {
      // User clicked cancel
    }
  } else {
    router.push('/customer')
  }
}

onMounted(async () => {
  window.addEventListener('resize', handleResize)
  
  if (props.loadData) {
    loading.value = true
    try {
      const data = await props.loadData()
      Object.assign(customerForm, data)
      initialFormState = JSON.stringify(customerForm)
      hasChanges.value = false
    } catch (error) {
      ElMessage.error(t('customer.form.loadDataFailed'))
    } finally {
      loading.value = false
    }
  } else {
    initForm()
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style lang="scss" scoped>
.customer-form-container {
  padding: 24px;
  background: #f0f2f5;
  min-height: calc(100vh - 140px);
  
  @media (max-width: 768px) {
    padding: 12px;
    min-height: calc(100vh - 100px);
  }
}

.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  padding: 0 4px;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }
  
  .back-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 500;
    color: #606266;
    padding: 8px 12px;
    border-radius: 4px;
    transition: all 0.2s;
    margin-right: 8px;

    @media (max-width: 768px) {
      padding: 6px 8px;
      font-size: 13px;
      margin-right: 0;
      
      .el-icon {
        font-size: 14px;
      }
    }

    &:hover {
      color: #409eff;
      background: #ecf5ff;
    }

    .el-icon {
      font-size: 16px;
    }
  }

  .page-title {
    flex: 1;
    font-size: 18px;
    font-weight: 600;
    
    @media (max-width: 768px) {
      font-size: 16px;
      flex: none;
      order: 3;
      width: 100%;
    }
  }
  
  :deep(.el-tag) {
    padding: 6px 16px;
    font-size: 14px;
    border-radius: 20px;
    
    @media (max-width: 768px) {
      padding: 4px 12px;
      font-size: 12px;
    }
  }
}

.form-card {
  background: #fff;
  border-radius: 8px;
  
  @media (max-width: 768px) {
    border-radius: 12px;
    
    :deep(.el-card__body) {
      padding: 16px 12px;
    }
  }
  
  .card-header {
    margin-bottom: 24px;
    
    @media (max-width: 768px) {
      margin-bottom: 16px;
    }
    
    .header-title {
      font-size: 15px;
      font-weight: 600;
      color: #333;
      
      @media (max-width: 768px) {
        font-size: 14px;
      }
    }
  }
}

.customer-form {
  max-width: 600px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
  
  :deep(.el-form-item__label) {
    font-weight: 500;
    color: #606266;
    
    @media (max-width: 768px) {
      padding-bottom: 4px;
      height: auto;
      line-height: 1.4;
    }
  }
  
  :deep(.el-input__inner),
  :deep(.el-textarea__inner) {
    border-radius: 4px;
    
    @media (max-width: 768px) {
      font-size: 16px; /* Prevent iOS zoom on input */
    }
  }
  
  :deep(.el-input-number) {
    width: 100%;
    
    @media (max-width: 768px) {
      :deep(.el-input-number__decrease),
      :deep(.el-input-number__increase) {
        width: 28px;
      }
      
      :deep(.el-input__wrapper) {
        padding-right: 58px;
      }
    }
  }
  
  :deep(.el-select) {
    width: 100%;
    
    @media (max-width: 768px) {
      :deep(.el-input__inner) {
        font-size: 16px;
      }
    }
  }
  
  :deep(.el-form-item) {
    @media (max-width: 768px) {
      margin-bottom: 18px;
    }
  }
  
  :deep(.el-switch__label) {
    font-size: 14px;
    
    @media (max-width: 768px) {
      font-size: 13px;
    }
  }
  
  :deep(.el-form-item__error) {
    @media (max-width: 768px) {
      font-size: 12px;
    }
  }
  
  .submit-btn {
    width: 140px;
    height: 40px;
    font-size: 15px;
    font-weight: 500;
    
    @media (max-width: 768px) {
      width: 100%;
      height: 44px;
      font-size: 16px;
    }
  }
  
  :deep(.el-form-item:last-child) {
    @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      gap: 12px;
      
      .el-button {
        width: 100%;
        height: 44px;
        font-size: 16px;
      }
    }
  }
}
</style>
